import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Container, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Chip,
  TextField,
  Autocomplete,
  CircularProgress,
  Avatar,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Strategy, MarketplaceStatus } from '../../types/strategy';
import { firestoreService } from '../../services/firestore';
import { useAuth } from '../../contexts/AuthContext';

export const Marketplace = () => {
  const { state: { user } } = useAuth();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [filteredStrategies, setFilteredStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    loadMarketplaceStrategies();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedTags, strategies, searchQuery]);

  // Filter strategies based on search query and selected tags
  const applyFilters = () => {
    const filtered = strategies.filter(strategy => {
      // Filter by search query (case insensitive)
      const matchesSearch = searchQuery === '' || 
        strategy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (strategy.description && strategy.description.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Filter by selected tags
      const matchesTags = selectedTags.length === 0 || 
        (strategy.tags && selectedTags.every(tag => strategy.tags!.includes(tag)));
      
      return matchesSearch && matchesTags;
    });
    
    setFilteredStrategies(filtered);
  };

  const loadMarketplaceStrategies = async () => {
    try {
      setLoading(true);
      const marketplaceStrategies = await firestoreService.getMarketplaceStrategies();
      setStrategies(marketplaceStrategies);
      
      const allTags = marketplaceStrategies.flatMap(s => s.tags || []);
      const uniqueTags = Array.from(new Set(allTags));
      setAvailableTags(uniqueTags);
    } catch (error) {
      console.error('Error loading marketplace strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTagChange = (event: any, value: string[]) => {
    setSelectedTags(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Strategy Marketplace
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Discover and use trading strategies created by the Strategiz community.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Search by name or description"
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title="Search">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          
          <Autocomplete
            multiple
            id="tags-filter"
            options={availableTags}
            value={selectedTags}
            onChange={handleTagChange}
            sx={{ minWidth: '50%', flexGrow: 1 }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                const { key, ...tagPropsWithoutKey } = tagProps;
                return (
                  <Chip key={index} label={option} {...tagPropsWithoutKey} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Filter by tags"
                placeholder="Select tags"
              />
            )}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredStrategies.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            my: 8, 
            p: 4, 
            border: '1px dashed', 
            borderColor: 'divider', 
            borderRadius: 2 
          }}>
            <Typography variant="h6" gutterBottom>No Strategies Available</Typography>
            <Typography variant="body1" color="textSecondary" align="center">
              There are no strategies available in the marketplace yet.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredStrategies.map((strategy) => (
              <Grid item xs={12} md={4} key={strategy.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>{strategy.name.charAt(0)}</Avatar>
                        <Typography variant="h6" sx={{ color: '#1976d2' }}>{strategy.name}</Typography>
                      </Box>
                      <Chip 
                        label={`by ${strategy.authorName || (strategy.userId ? strategy.userId.substring(0, 8) : 'Anonymous')}`} 
                        size="small" 
                        variant="outlined"
                        sx={{ ml: 1 }} 
                      />
                    </Box>
                    
                    <Typography color="textSecondary" variant="body2" sx={{ mb: 2, minHeight: '40px' }}>
                      {strategy.description || 'No description provided.'}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Mode: {strategy.mode} | Type: {strategy.type}
                    </Typography>
                    
                    {strategy.tags && strategy.tags.length > 0 && (
                      <Box sx={{ mt: 1, mb: 2 }}>
                        {strategy.tags.map((tag, index) => (
                          <Chip 
                            key={index} 
                            label={tag} 
                            size="small" 
                            sx={{ mr: 0.5, mb: 0.5 }} 
                          />
                        ))}
                      </Box>
                    )}
                    
                    {strategy.backtestResults && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="subtitle2">Performance:</Typography>
                        <Typography variant="body2">
                          Returns: {strategy.backtestResults.totalReturns}% | 
                          Sharpe: {strategy.backtestResults.sharpeRatio.toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                    
                    <Button 
                      variant="contained" 
                      fullWidth
                      color="primary"
                      sx={{ mt: 1 }}
                    >
                      Use This Strategy
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};
